import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './Filter.module.css';

const DIFFICULTIES = [
  'F', 'PD-', 'PD', 'PD+', 'AD-', 'AD', 'AD+',
  'D-', 'D', 'D+', 'TD-', 'TD', 'TD+',
  'ED-', 'ED', 'ED+', 'ABO'
];

function RouteFilter({ filter, onFilterChange, onResetFilters, mountains, sortOrder, onSortChange }) {
  const { t } = useTranslation();
  const [isPopupOpen, setIsPopupOpen] = useState(false);

  // Extract unique countries and ranges from the provided mountains
  const countries = [...new Set(mountains.map(m => m.country).filter(Boolean))];
  const ranges = [...new Set(mountains.map(m => m.mountainRange).filter(Boolean))];

  // Hardcoded categories for Length and Elevation
  const lengthCategories = {
    short: { label: t('browse.filter.lengths.short', '< 5 km'), min: 0, max: 5 },
    medium: { label: t('browse.filter.lengths.medium', '5 - 10 km'), min: 5, max: 10 },
    long: { label: t('browse.filter.lengths.long', '10 - 20 km'), min: 10, max: 20 },
    epic: { label: t('browse.filter.lengths.epic', '> 20 km'), min: 20, max: 9999 }
  };

  const elevationCategories = {
    flattish: { label: t('browse.filter.elevations.flattish', '< 200 m'), min: 0, max: 200 },
    mild: { label: t('browse.filter.elevations.mild', '200 - 500 m'), min: 200, max: 500 },
    challenging: { label: t('browse.filter.elevations.challenging', '500 - 1000 m'), min: 500, max: 1000 },
    extreme: { label: t('browse.filter.elevations.extreme', '> 1000 m'), min: 1000, max: 9999 }
  };

  const togglePopup = () => {
    setIsPopupOpen(!isPopupOpen);
  };

  const handleApplyFilters = () => {
    setIsPopupOpen(false);
  };

  const handleResetAndClose = () => {
    onResetFilters();
    setIsPopupOpen(false);
  };

  return (
    <div className={styles.filterContainer}>
      {/* Trigger Button */}
      <button
        className={styles.filterTriggerBtn}
        onClick={togglePopup}
        aria-label="Open Filters"
      >
        <svg style={{height: '1.5rem', width: '1.5rem'}} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 3c2.755 0 5.455.232 8.083.678.533.09.917.556.917 1.096v1.044a2.25 2.25 0 0 1-.659 1.591l-5.432 5.432a2.25 2.25 0 0 0-.659 1.591v2.927a2.25 2.25 0 0 1-1.244 2.013L9.75 21v-6.568a2.25 2.25 0 0 0-.659-1.591L3.659 7.409A2.25 2.25 0 0 1 3 5.818V4.774c0-.54.384-1.006.917-1.096A48.32 48.32 0 0 1 12 3Z" />
        </svg>
        {Object.values(filter).some(value => value !== '' && value !== null) && (
          <span className={styles.filterIndicator}>•</span>
        )}
      </button>

      {/* Popup */}
      {isPopupOpen && (
        <div className={styles.filterPopupOverlay} onClick={togglePopup}>
          <div className={styles.filterPopup} onClick={(e) => e.stopPropagation()}>
            <div className={styles.filterPopupHeader}>
              <h3>{t('browse.filter.title', 'Filters')}</h3>
              <button className={styles.closePopupBtn} onClick={togglePopup}>×</button>
            </div>

            <div className={styles.filterPopupContent}>
              
              {/* Country */}
              <div className={styles.filterGroup}>
                <label className={styles.filterLabel}>{t('browse.filter.countryLabel')}</label>
                <select
                  value={filter.country || ''}
                  onChange={(e) => onFilterChange('country', e.target.value)}
                  className={styles.filterSelect}
                >
                  <option value="">{t('browse.filter.countryAll', 'All Countries')}</option>
                  {countries.map(country => (
                    <option key={country} value={country}>{country}</option>
                  ))}
                </select>
              </div>

              {/* Mountain Range */}
              <div className={styles.filterGroup}>
                <label className={styles.filterLabel}>{t('browse.filter.rangeLabel')}</label>
                <select
                  value={filter.mountainRange || ''}
                  onChange={(e) => onFilterChange('mountainRange', e.target.value)}
                  className={styles.filterSelect}
                >
                  <option value="">{t('browse.filter.rangeAll', 'All Ranges')}</option>
                  {ranges.map(range => (
                    <option key={range} value={range}>{range}</option>
                  ))}
                </select>
              </div>

              {/* Length Category */}
              <div className={styles.filterGroup}>
                <label className={styles.filterLabel}>{t('browse.filter.lengthLabel')}</label>
                <div className={styles.radioGroup}>
                    <label className={styles.radioLabel}>
                        <input
                            type="radio"
                            name="lengthCategory"
                            value=""
                            checked={filter.lengthCategory === ''}
                            onChange={(e) => onFilterChange('lengthCategory', e.target.value)}
                            className={styles.radioInput}
                        />
                        <span className={styles.radioText}>{t('browse.filter.lengthAll', 'All Lengths')}</span>
                    </label>
                    {Object.entries(lengthCategories).map(([key, cat]) => (
                        <label key={key} className={styles.radioLabel}>
                            <input
                                type="radio"
                                name="lengthCategory"
                                value={key}
                                checked={filter.lengthCategory === key}
                                onChange={(e) => onFilterChange('lengthCategory', e.target.value)}
                                className={styles.radioInput}
                            />
                            <span className={styles.radioText}>{cat.label}</span>
                        </label>
                    ))}
                </div>
              </div>

              {/* Elevation Category */}
              <div className={styles.filterGroup}>
                <label className={styles.filterLabel}>{t('browse.filter.elevationLabel')}</label>
                <div className={styles.radioGroup}>
                    <label className={styles.radioLabel}>
                        <input
                            type="radio"
                            name="elevationCategory"
                            value=""
                            checked={filter.elevationCategory === ''}
                            onChange={(e) => onFilterChange('elevationCategory', e.target.value)}
                            className={styles.radioInput}
                        />
                        <span className={styles.radioText}>{t('browse.filter.elevationAll', 'All Elevations')}</span>
                    </label>
                    {Object.entries(elevationCategories).map(([key, cat]) => (
                        <label key={key} className={styles.radioLabel}>
                            <input
                                type="radio"
                                name="elevationCategory"
                                value={key}
                                checked={filter.elevationCategory === key}
                                onChange={(e) => onFilterChange('elevationCategory', e.target.value)}
                                className={styles.radioInput}
                            />
                            <span className={styles.radioText}>{cat.label}</span>
                        </label>
                    ))}
                </div>
              </div>
              
              {/* Difficulty Filter */}
              <div className={styles.filterGroup}>
                <label className={styles.filterLabel}>{t('browse.filter.difficultyRouteLabel')}:</label>
                 <select
                  value={filter.difficulty || ''}
                  onChange={(e) => onFilterChange('difficulty', e.target.value)}
                  style={{ width: '100%', padding: '0.5rem', borderRadius: '8px', border: '1px solid #ccc' }}
                >
                  <option value="">{t('browse.filter.types.any')}</option>
                  {DIFFICULTIES.map(diff => (
                    <option key={diff} value={diff}>{diff}</option>
                  ))}
                </select>
              </div>

              {/* Route Type Filter */}
              <div className={styles.filterGroup}>
                <label className={styles.filterLabel}>{t('browse.filter.typeLabel')}:</label>
                 <select
                  value={filter.routeType || ''}
                  onChange={(e) => onFilterChange('routeType', e.target.value)}
                  style={{ width: '100%', padding: '0.5rem', borderRadius: '8px', border: '1px solid #ccc' }}
                >
                  <option value="">{t('browse.filter.types.any')}</option>
                  <option value="one-day">{t('browse.filter.types.oneDay')}</option>
                  <option value="multi-day-huts">{t('browse.filter.types.multiDayHuts')}</option>
                </select>
              </div>

              {/* Sort Order */}
              <div className={styles.filterGroup}>
                <label className={styles.filterLabel} style={{marginTop: '2rem'}}>{t('browse.filter.sortBy')}</label>
                <div className={styles.radioGroup}>
                   <label className={styles.radioLabel}>
                    <input type="radio" name="sortOrder" value="elevation-desc" checked={sortOrder === 'elevation-desc'} onChange={(e) => onSortChange(e.target.value)} className={styles.radioInput} />
                    <span className={styles.radioText}>{t('browse.filter.sortElevationDesc')}</span>
                   </label>
                   <label className={styles.radioLabel}>
                    <input type="radio" name="sortOrder" value="elevation-asc" checked={sortOrder === 'elevation-asc'} onChange={(e) => onSortChange(e.target.value)} className={styles.radioInput} />
                    <span className={styles.radioText}>{t('browse.filter.sortElevationAsc')}</span>
                   </label>
                   <label className={styles.radioLabel}>
                    <input type="radio" name="sortOrder" value="length-desc" checked={sortOrder === 'length-desc'} onChange={(e) => onSortChange(e.target.value)} className={styles.radioInput} />
                    <span className={styles.radioText}>{t('browse.filter.sortLengthDesc')}</span>
                   </label>
                   <label className={styles.radioLabel}>
                    <input type="radio" name="sortOrder" value="length-asc" checked={sortOrder === 'length-asc'} onChange={(e) => onSortChange(e.target.value)} className={styles.radioInput} />
                    <span className={styles.radioText}>{t('browse.filter.sortLengthAsc')}</span>
                   </label>
                </div>
              </div>

            </div>
            
            <div className={styles.filterPopupActions}>
              <button className={styles.btnSecondary} onClick={handleResetAndClose}>{t('common.reset', 'Reset')}</button>
              <button className={styles.btnPrimary} onClick={handleApplyFilters}>{t('common.apply', 'Apply')}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default RouteFilter;
